import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import rawCatalog from '../data/help-content.v1.json';
import type { HelpContentCatalog } from '../types/product-guidance';
import { ACE_ICON_CLASS } from '../visuals';
import { recordProductGuidanceEventBestEffort } from '../api/product-guidance-analytics.api';
import { launchGuidedProductTour } from '../../product-tour/navigation/launch-product-tour';

const catalog = rawCatalog as HelpContentCatalog;
const FEATURED_TASKS = ['create-first-machine', 'choose-documents', 'upload-documents', 'ask-better-question', 'understand-evidence', 'question-vs-investigation'];

export function HelpCenterPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const normalized = query.trim().toLocaleLowerCase();
  const matches = useMemo(() => {
    if (!normalized) return [];
    return catalog.articles.filter((article) => [article.title, article.summary, article.body, ...article.keywords].join(' ').toLocaleLowerCase().includes(normalized)).slice(0, 12);
  }, [normalized]);
  const areas = useMemo(() => [...new Set(catalog.articles.map((article) => article.product_area))].sort(), []);
  const lastTrackedSearch = useRef('');

  function openProductTour() {
    const returnPath = `${location.pathname}${location.search}${location.hash}`;
    launchGuidedProductTour(returnPath, navigate, 'HELP_CENTER');
  }

  useEffect(() => {
    if (!normalized) return;
    const timer = window.setTimeout(() => {
      const key = `${normalized}|${matches.length}`;
      if (lastTrackedSearch.current === key) return;
      lastTrackedSearch.current = key;
      recordProductGuidanceEventBestEffort({ event_type: 'HELP_SEARCH', query: query.trim(), result_count: matches.length });
    }, 650);
    return () => window.clearTimeout(timer);
  }, [matches.length, normalized, query]);

  return (
    <section className="r42-help-page">
      <header className="r42-help-hero">
        <span className="r42-eyebrow">Route42 Help</span>
        <h1>What would you like to do?</h1>
        <p>Find a clear answer without learning a manual first.</p>
        <label className="r42-help-search">
          <i className="bi bi-search" aria-hidden="true" />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search how to use Route42…" aria-label="Search Route42 Help" />
          {query ? <button type="button" onClick={() => setQuery('')} aria-label="Clear search"><i className="bi bi-x-lg" aria-hidden="true" /></button> : null}
        </label>
      </header>

      {!normalized ? (
        <section className="r42-help-tour-card" aria-labelledby="r42-help-tour-title">
          <div className="r42-help-tour-card__icon" aria-hidden="true"><i className="bi bi-signpost-split" /></div>
          <div className="r42-help-tour-card__copy">
            <span className="r42-eyebrow">Guided example</span>
            <h2 id="r42-help-tour-title">See Route42 in action</h2>
            <p>Walk through one machine to see how evidence, Conversations, Investigations, and Machine Memory work together.</p>
            <div className="r42-help-tour-card__meta"><span><i className="bi bi-clock" aria-hidden="true" /> About 3 minutes</span><span><i className="bi bi-shield-check" aria-hidden="true" /> Fictional, read-only data</span></div>
          </div>
          <div className="r42-help-tour-card__actions">
            <button className="btn r42-btn-primary r42-help-tour-card__action" type="button" onClick={openProductTour}>
              Show me how Route42 works <i className="bi bi-arrow-right" aria-hidden="true" />
            </button>
            <Link className="btn btn-outline-secondary" to="/learn/investigations">See an Investigation in action</Link>
          </div>
        </section>
      ) : null}

      {normalized ? (
        <div className="r42-help-results">
          <div className="r42-help-section-head"><div><span className="r42-eyebrow">Search results</span><h2>{matches.length ? `Help for “${query.trim()}”` : 'No exact match yet'}</h2></div></div>
          {matches.length ? <div className="r42-help-list">{matches.map((article) => <HelpRow key={article.id} article={article} />)}</div> : <div className="r42-help-no-results"><i className="bi bi-compass" aria-hidden="true" /><strong>Try describing the task in your own words.</strong><p>Ace can also answer application questions from anywhere in Route42.</p></div>}
        </div>
      ) : (
        <>
          <section className="r42-help-section">
            <div className="r42-help-section-head"><div><span className="r42-eyebrow">Start here</span><h2>Common things people want to do</h2></div></div>
            <div className="r42-help-featured">
              {catalog.articles.filter((article) => article.tasks.some((task) => FEATURED_TASKS.includes(task))).slice(0, 6).map((article) => (
                <Link key={article.id} to={`/help/${article.id}`}><i className={helpIcon(article.product_area)} aria-hidden="true" /><div><strong>{article.title}</strong><span>{article.summary}</span></div><i className="bi bi-arrow-right" aria-hidden="true" /></Link>
              ))}
            </div>
          </section>

          <section className="r42-help-section">
            <div className="r42-help-section-head"><div><span className="r42-eyebrow">Browse</span><h2>Help by topic</h2></div><span>{catalog.article_count} guides</span></div>
            <div className="r42-help-topics">
              {areas.map((area) => {
                const articles = catalog.articles.filter((article) => article.product_area === area);
                return <div key={area} className="r42-help-topic-card"><i className={helpIcon(area)} aria-hidden="true" /><div><strong>{areaLabel(area)}</strong><span>{articles.length} {articles.length === 1 ? 'guide' : 'guides'}</span></div><div className="r42-help-topic-links">{articles.slice(0, 3).map((article) => <Link key={article.id} to={`/help/${article.id}`}>{article.title}</Link>)}</div></div>;
              })}
            </div>
          </section>
        </>
      )}
    </section>
  );
}

function HelpRow({ article }: { article: HelpContentCatalog['articles'][number] }) {
  return <Link className="r42-help-row" to={`/help/${article.id}`}><div><strong>{article.title}</strong><span>{article.summary}</span></div><i className="bi bi-chevron-right" aria-hidden="true" /></Link>;
}

export function areaLabel(area: string) {
  const labels: Record<string, string> = { ace: 'Ask Ace', 'machine-memory': 'Machine Memory', 'getting-started': 'Getting started', users: 'Users & access' };
  return labels[area] ?? area.split('-').map((part) => part[0]?.toUpperCase() + part.slice(1)).join(' ');
}

export function helpIcon(area: string) {
  const icons: Record<string, string> = { 'getting-started': 'bi bi-stars', machines: 'bi bi-gear-wide-connected', documents: 'bi bi-file-earmark-text', ace: ACE_ICON_CLASS, investigations: 'bi bi-search', 'machine-memory': 'bi bi-cpu', activity: 'bi bi-clock-history', facilities: 'bi bi-building', users: 'bi bi-people', billing: 'bi bi-credit-card', profile: 'bi bi-person', permissions: 'bi bi-shield-check', terminology: 'bi bi-book' };
  return icons[area] ?? 'bi bi-question-circle';
}
